/**
 * Organisation Settings — Satellite Monitoring Frequency
 *
 * Configures how often the automated Sentinel-2 / GEE satellite sweep runs
 * for this organisation:
 *   • frequency              — daily / every 3 days / weekly / fortnightly / manual only
 *   • preferred_utc_hour     — preferred UTC start time for the automated sweep
 *   • rescan_suppress_days   — minimum gap before a boundary is re-swept
 *   • max_cloud_cover        — Sentinel-2 cloud-cover threshold (0–50%)
 *   • allow_manual_trigger   — whether operators can trigger a sweep from the UI
 *   • verticals              — limit the sweep to specific verticals (empty = all)
 *
 * Persisted to organizations.settings->'satellite_monitoring' via
 * PATCH /api/organizations/:id/settings.
 *
 * The job (satellite-monitoring.job.ts) reads these values at sweep time.
 * The Cloud Scheduler cron remains the global heartbeat; this setting tells
 * the job whether to actually run for a given org at that tick.
 */
import React, { useState, useEffect } from 'react';
import {
  Satellite, Clock, CloudOff, ToggleLeft, ToggleRight,
  Save, RotateCcw, Info, Layers, RefreshCw,
} from 'lucide-react';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Alert from '../../components/common/Alert';
import Spinner from '../../components/common/Spinner';
import {
  useAuthStore,
  DEFAULT_SATELLITE_MONITORING,
  OrgSatelliteMonitoringSettings,
  ScanFrequency,
  ScanTimeWindow,
  ScanCloudCoverLimit,
} from '../../store/authStore';
import { organizationsApi } from '../../services/api';

// ─── Option definitions ───────────────────────────────────────────────────────

const FREQUENCY_OPTIONS: {
  value: ScanFrequency;
  label: string;
  description: string;
  badge?: string;
  cronHint: string;
}[] = [
  {
    value: 'daily',
    label: 'Daily',
    description: 'Sentinel-2 pass every 24 hours. Best for fast-changing environments (active construction, wildfire monitoring). Higher API usage.',
    cronHint: '0 {H} * * *',
  },
  {
    value: 'every_3_days',
    label: 'Every 3 days',
    description: 'Matches the approximate Sentinel-2 revisit frequency for most latitudes. Good balance of freshness and compute cost.',
    cronHint: '0 {H} */3 * *',
  },
  {
    value: 'weekly',
    label: 'Weekly',
    badge: 'Default',
    description: 'Automated sweep runs once a week (Mondays at your preferred UTC hour). Suitable for most agricultural and infrastructure use cases.',
    cronHint: '0 {H} * * 1',
  },
  {
    value: 'fortnightly',
    label: 'Fortnightly',
    description: 'Sweep runs every two weeks. Appropriate for slow-changing environments such as carbon credit forest monitoring.',
    cronHint: '0 {H} 1,15 * *',
  },
  {
    value: 'manual_only',
    label: 'Manual only',
    description: 'No automated sweep. Operators trigger scans on-demand from the Anomaly Alerts screen using the "Run Monitoring" button.',
    cronHint: 'Disabled',
  },
];

const TIME_OPTIONS: { value: ScanTimeWindow; label: string }[] = [
  { value: '00:00', label: '00:00 UTC (midnight)' },
  { value: '03:00', label: '03:00 UTC — default' },
  { value: '06:00', label: '06:00 UTC (early morning)' },
  { value: '12:00', label: '12:00 UTC (midday)' },
  { value: '18:00', label: '18:00 UTC (evening)' },
];

const CLOUD_COVER_OPTIONS: { value: ScanCloudCoverLimit; label: string; hint: string }[] = [
  { value: 10, label: '≤ 10%',  hint: 'Strictest — only near-clear scenes used. May skip scans in cloudy climates.' },
  { value: 20, label: '≤ 20%',  hint: 'Recommended — good balance of image quality vs. availability.' },
  { value: 30, label: '≤ 30%',  hint: 'Moderate — more scans pass but some edge haze may affect NDVI accuracy.' },
  { value: 40, label: '≤ 40%',  hint: 'Lenient — use only if your region is frequently overcast.' },
  { value: 50, label: '≤ 50%',  hint: 'Maximum — half-cloudy scenes accepted. Not recommended for precision analytics.' },
];

const ALL_VERTICALS = [
  { key: 'agrotech',        label: 'AgroTech' },
  { key: 'oil_gas',         label: 'Oil & Gas' },
  { key: 'infrastructure',  label: 'Infrastructure' },
  { key: 'environmental',   label: 'Environmental' },
  { key: 'linear_assets',   label: 'Linear Assets' },
];

// ─── Helper components ────────────────────────────────────────────────────────

function SectionHeader({ label, hint }: { label: string; hint?: string }) {
  return (
    <div className="mb-4">
      <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
        {label}
      </h2>
      {hint && (
        <p className="mt-0.5 text-xs text-gray-400 dark:text-gray-500 flex items-start gap-1.5">
          <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />{hint}
        </p>
      )}
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function SatelliteMonitoringSettingsPage() {
  const { currentOrganizationId, orgSatelliteMonitoring, setOrgSatelliteMonitoring } = useAuthStore();

  const [form, setForm]       = useState<OrgSatelliteMonitoringSettings>({ ...DEFAULT_SATELLITE_MONITORING });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  // Scheduler sync result from the API response
  const [schedulerInfo, setSchedulerInfo] = useState<{ cron: string | null; status: string } | null>(null);

  // ── Load ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    const load = async () => {
      if (!currentOrganizationId) return;
      setLoading(true);
      try {
        const res = await organizationsApi.getMyOrg(currentOrganizationId);
        const org = res.data ?? res;
        const saved: Partial<OrgSatelliteMonitoringSettings> = org?.settings?.satellite_monitoring ?? {};
        setForm({ ...DEFAULT_SATELLITE_MONITORING, ...saved });
      } catch {
        setForm({ ...orgSatelliteMonitoring });
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrganizationId]);

  const setField = <K extends keyof OrgSatelliteMonitoringSettings>(
    k: K,
    v: OrgSatelliteMonitoringSettings[K]
  ) => setForm(prev => ({ ...prev, [k]: v }));

  const toggleVertical = (key: string) => {
    setForm(prev => {
      const current = prev.verticals ?? [];
      const next = current.includes(key)
        ? current.filter(v => v !== key)
        : [...current, key];
      return { ...prev, verticals: next };
    });
  };

  // ── Save ──────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!currentOrganizationId) return;
    setSaving(true); setError(null); setSuccess(null); setSchedulerInfo(null);
    try {
      const res = await organizationsApi.updateSettings(currentOrganizationId, { satellite_monitoring: form });
      setOrgSatelliteMonitoring(form);
      // Surface the Cloud Scheduler sync result if the API returned one
      const scheduler = (res?.data ?? res)?.scheduler as { cron: string | null; status: string } | undefined;
      if (scheduler) setSchedulerInfo(scheduler);
      setSuccess('Satellite monitoring settings saved — the next scheduled sweep will use this configuration.');
    } catch (e: any) {
      setError(e?.response?.data?.error ?? e?.response?.data?.message ?? 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => setForm({ ...DEFAULT_SATELLITE_MONITORING });

  // Derived
  const selectedFrequency = FREQUENCY_OPTIONS.find(f => f.value === form.frequency)!;
  const isManualOnly      = form.frequency === 'manual_only';
  const allVerticalsActive = form.verticals.length === 0;

  const effectiveCron = isManualOnly
    ? 'Disabled — no automated sweep'
    : selectedFrequency.cronHint.replace('{H}', form.preferred_utc_hour.split(':')[0]);

  if (loading) return <div className="flex justify-center py-16"><Spinner size="lg" /></div>;

  return (
    <div className="space-y-6 max-w-3xl">

      {/* ── Header ── */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <Satellite className="w-6 h-6 text-sky-500" /> Satellite Monitoring Schedule
        </h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Control how often Nabhos runs an automated Sentinel-2 / GEE sweep across your monitored boundaries.
          The platform scheduler runs globally; these settings tell it what to do for your organisation at each tick.
        </p>
      </div>

      {error   && <Alert type="error"   onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert type="success" onClose={() => setSuccess(null)}>{success}</Alert>}

      {/* ── Cloud Scheduler sync result (shown after save) ── */}
      {schedulerInfo && (
        <div className={`flex items-start gap-3 px-4 py-3 rounded-lg border text-xs ${
          schedulerInfo.status.startsWith('error')
            ? 'bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-800 text-red-700 dark:text-red-300'
            : schedulerInfo.status.startsWith('skipped')
            ? 'bg-amber-50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-800 text-amber-700 dark:text-amber-300'
            : 'bg-sky-50 dark:bg-sky-950/20 border-sky-200 dark:border-sky-800 text-sky-700 dark:text-sky-300'
        }`}>
          <Satellite className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <div className="space-y-0.5">
            <p className="font-semibold">
              {schedulerInfo.status.startsWith('error')  ? 'Cloud Scheduler update failed' :
               schedulerInfo.status.startsWith('skipped') ? 'Cloud Scheduler not configured' :
               schedulerInfo.status === 'created'         ? 'Cloud Scheduler job created' :
               schedulerInfo.status === 'updated'         ? 'Cloud Scheduler job updated' :
               'Cloud Scheduler job removed'}
            </p>
            <p>
              {schedulerInfo.status.startsWith('skipped')
                ? 'Set GCP_PROJECT_ID, GCP_LOCATION, and CLOUD_RUN_SERVICE_URL on the API server to enable automatic schedule updates.'
                : schedulerInfo.status.startsWith('error')
                ? schedulerInfo.status.replace('error: ', '')
                : schedulerInfo.cron
                ? <>Effective cron: <code className="font-mono bg-sky-100 dark:bg-sky-900/30 px-1 rounded">{schedulerInfo.cron}</code></>
                : 'No GCP scheduler job — runtime behaviour applies (manual_only / global weekly sweep).'}
            </p>
          </div>
        </div>
      )}

      {/* ── Scan frequency ── */}
      <Card>
        <SectionHeader
          label="Scan Frequency"
          hint="How often the automated sweep checks each of your monitored boundaries."
        />
        <div className="space-y-3">
          {FREQUENCY_OPTIONS.map(opt => {
            const selected = form.frequency === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => setField('frequency', opt.value)}
                className={[
                  'w-full text-left p-4 rounded-xl border-2 transition-all focus:outline-none focus:ring-2 focus:ring-primary-500',
                  selected
                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                    : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-600',
                ].join(' ')}
              >
                <div className="flex items-start gap-3">
                  <span className={`mt-0.5 w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${selected ? 'border-primary-500' : 'border-gray-400'}`}>
                    {selected && <span className="w-2 h-2 rounded-full bg-primary-500 block" />}
                  </span>
                  <div className="flex-1 min-w-0">
                    <span className="text-sm font-medium text-gray-900 dark:text-white flex items-center gap-2 flex-wrap">
                      {opt.label}
                      {opt.badge && (
                        <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300">
                          {opt.badge}
                        </span>
                      )}
                    </span>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
                      {opt.description}
                    </p>
                    {selected && !isManualOnly && (
                      <p className="mt-1.5 text-[11px] font-mono text-sky-600 dark:text-sky-400">
                        Cron: {opt.cronHint.replace('{H}', form.preferred_utc_hour.split(':')[0])}
                      </p>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </Card>

      {/* ── Preferred UTC sweep time — hidden when manual only ── */}
      {!isManualOnly && (
        <Card>
          <SectionHeader
            label="Preferred Sweep Time (UTC)"
            hint="The Cloud Scheduler cron will be aligned to this UTC hour. Choose off-peak hours to avoid competing with daytime API load."
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {TIME_OPTIONS.map(opt => {
              const selected = form.preferred_utc_hour === opt.value;
              return (
                <label
                  key={opt.value}
                  className={[
                    'flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all',
                    selected
                      ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                      : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600',
                  ].join(' ')}
                >
                  <input
                    type="radio"
                    name="preferred_utc_hour"
                    value={opt.value}
                    checked={selected}
                    onChange={() => setField('preferred_utc_hour', opt.value)}
                    className="accent-primary-500 flex-shrink-0"
                  />
                  <span className="text-sm font-medium text-gray-900 dark:text-white flex items-center gap-1.5">
                    <Clock className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                    {opt.label}
                  </span>
                </label>
              );
            })}
          </div>
        </Card>
      )}

      {/* ── Cloud cover threshold ── */}
      {!isManualOnly && (
        <Card>
          <SectionHeader
            label="Maximum Cloud Cover"
            hint="Sentinel-2 scenes above this cloud-cover percentage are rejected. A lower threshold means cleaner data but fewer usable scenes."
          />
          <div className="space-y-2">
            {CLOUD_COVER_OPTIONS.map(opt => {
              const selected = form.max_cloud_cover === opt.value;
              return (
                <label
                  key={opt.value}
                  className={[
                    'flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all',
                    selected
                      ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                      : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600',
                  ].join(' ')}
                >
                  <input
                    type="radio"
                    name="max_cloud_cover"
                    value={opt.value}
                    checked={selected}
                    onChange={() => setField('max_cloud_cover', opt.value)}
                    className="mt-0.5 accent-primary-500 flex-shrink-0"
                  />
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white flex items-center gap-1.5">
                      <CloudOff className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                      {opt.label}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{opt.hint}</p>
                  </div>
                </label>
              );
            })}
          </div>
        </Card>
      )}

      {/* ── Re-scan suppression window ── */}
      {!isManualOnly && (
        <Card>
          <SectionHeader
            label="Re-scan Suppression Window"
            hint="A boundary already swept within this many days is skipped — even if the scheduler fires. Prevents duplicate scans when you trigger manually shortly before the scheduled run."
          />
          <div className="flex items-center gap-4">
            <input
              type="number"
              min={1}
              max={30}
              value={form.rescan_suppress_days}
              onChange={e => setField('rescan_suppress_days', Math.max(1, Math.min(30, Number(e.target.value))))}
              className="w-24 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm font-bold focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            <span className="text-sm text-gray-500 dark:text-gray-400">days</span>
            <input
              type="range"
              min={1}
              max={30}
              value={form.rescan_suppress_days}
              onChange={e => setField('rescan_suppress_days', Number(e.target.value))}
              className="flex-1 accent-sky-500"
            />
          </div>
          <p className="mt-2 text-xs text-gray-400">
            Current: skip boundaries swept in the last <strong>{form.rescan_suppress_days} day{form.rescan_suppress_days !== 1 ? 's' : ''}</strong>
          </p>
        </Card>
      )}

      {/* ── Verticals scope ── */}
      <Card>
        <SectionHeader
          label="Verticals in Scope"
          hint="Restrict the automated sweep to specific verticals. Leave all unchecked to sweep every vertical your organisation has active."
        />
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-3">
          {ALL_VERTICALS.map(v => {
            const active = form.verticals.includes(v.key);
            return (
              <label
                key={v.key}
                className={[
                  'flex items-center gap-2.5 p-3 rounded-lg border-2 cursor-pointer text-sm font-medium transition-all',
                  active
                    ? 'border-sky-500 bg-sky-50 dark:bg-sky-950/20 text-sky-700 dark:text-sky-300'
                    : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:border-gray-300',
                ].join(' ')}
              >
                <input
                  type="checkbox"
                  checked={active}
                  onChange={() => toggleVertical(v.key)}
                  className="accent-sky-500 flex-shrink-0"
                />
                <Layers className="w-3.5 h-3.5 flex-shrink-0 opacity-60" />
                {v.label}
              </label>
            );
          })}
        </div>
        <p className="text-xs text-gray-400">
          {allVerticalsActive
            ? 'All verticals will be swept (no restriction).'
            : `Only sweeping: ${form.verticals.map(k => ALL_VERTICALS.find(v => v.key === k)?.label ?? k).join(', ')}.`}
        </p>
      </Card>

      {/* ── Manual trigger toggle ── */}
      <Card>
        <SectionHeader
          label="Manual Trigger"
          hint="Whether operators can click 'Run Monitoring' in the Anomaly Alerts screens to trigger an on-demand scan."
        />
        <button
          type="button"
          onClick={() => setField('allow_manual_trigger', !form.allow_manual_trigger)}
          className="flex items-center gap-3 w-full text-left p-4 rounded-xl border-2 border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-600 transition-all focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          {form.allow_manual_trigger
            ? <ToggleRight className="w-7 h-7 text-primary-500 flex-shrink-0" />
            : <ToggleLeft  className="w-7 h-7 text-gray-400 flex-shrink-0" />
          }
          <div>
            <p className="text-sm font-medium text-gray-900 dark:text-white">
              {form.allow_manual_trigger ? 'Manual trigger allowed' : 'Automated schedule only'}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              {form.allow_manual_trigger
                ? 'Operators can trigger an on-demand scan from any Anomaly Alerts screen at any time.'
                : 'Only the automated schedule runs scans. The "Run Monitoring" button is disabled for operators.'}
            </p>
          </div>
        </button>
      </Card>

      {/* ── Configuration summary ── */}
      <Card>
        <SectionHeader label="Effective Configuration" />
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          {[
            { icon: RefreshCw, label: 'Frequency',         value: selectedFrequency.label },
            { icon: Clock,     label: 'UTC Hour',          value: isManualOnly ? 'N/A' : form.preferred_utc_hour },
            { icon: CloudOff,  label: 'Cloud Cover Max',   value: isManualOnly ? 'N/A' : `${form.max_cloud_cover}%` },
            { icon: Satellite, label: 'Suppress Window',   value: isManualOnly ? 'N/A' : `${form.rescan_suppress_days}d` },
            { icon: Layers,    label: 'Verticals',         value: allVerticalsActive ? 'All' : `${form.verticals.length} selected` },
            { icon: form.allow_manual_trigger ? ToggleRight : ToggleLeft,
              label: 'Manual Trigger',
              value: form.allow_manual_trigger ? 'Enabled' : 'Disabled' },
          ].map(({ icon: Icon, label, value }) => (
            <div key={label} className="p-3 rounded-lg bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-1.5 mb-1">
                <Icon className="w-3.5 h-3.5 text-gray-400" />
                <span className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">{label}</span>
              </div>
              <p className="text-sm font-bold text-gray-900 dark:text-white">{value}</p>
            </div>
          ))}
        </div>
        {!isManualOnly && (
          <p className="mt-3 text-xs font-mono text-sky-600 dark:text-sky-400 bg-sky-50 dark:bg-sky-950/20 px-3 py-2 rounded-lg border border-sky-100 dark:border-sky-900">
            Effective cron: <strong>{effectiveCron}</strong>
          </p>
        )}
      </Card>

      {/* ── Actions ── */}
      <div className="flex items-center gap-3">
        <Button onClick={handleSave} loading={saving} disabled={saving}>
          <Save className="w-4 h-4 mr-1.5" />Save Settings
        </Button>
        <Button variant="outline" onClick={handleReset} disabled={saving}>
          <RotateCcw className="w-4 h-4 mr-1.5" />Reset to Defaults
        </Button>
      </div>

    </div>
  );
}

// Made with Bob
